import { Bot, Copy, Database, Edit2, Loader2, MessageSquare, Trash2 } from "lucide-react";
import type { MouseEvent } from "react";
import { AgentProjectBadge } from "@/components/shared";
import type { Agent } from "@/types";

export interface AgentCardProps {
  agent: Agent;
  /** UUID of a just-created agent, to show a transient "New" badge. */
  newlyCreatedUuid?: string | null;
  /** UUID currently being deleted (drives the spinner on the delete button). */
  deletingUuid?: string | null;
  onCardClick: (agent: Agent) => void;
  onStartChat: (agent: Agent, e: MouseEvent) => void;
  onDuplicate: (agent: Agent, e: MouseEvent) => void;
  onEdit: (agent: Agent, e: MouseEvent) => void;
  onDelete: (agentUuid: string, e: MouseEvent) => void;
}

/**
 * A single agent tile in the agents grid.
 *
 * The whole tile opens the agent detail. Rather than making the container a
 * `role="button"` (which would invalidly wrap the nested action buttons), a
 * transparent full-bleed `<button>` overlay handles the "open" click/keyboard
 * behaviour, and the action buttons sit above it (higher stacking context) so
 * they remain independently focusable and are not nested inside another button.
 */
export function AgentCard({
  agent,
  newlyCreatedUuid,
  deletingUuid,
  onCardClick,
  onStartChat,
  onDuplicate,
  onEdit,
  onDelete,
}: Readonly<AgentCardProps>) {
  return (
    <div className="relative bg-surface-primary border border-border-primary rounded-xl p-6 hover:border-border-secondary transition group flex flex-col">
      {/* Full-card click/keyboard target for opening the detail view. */}
      <button
        type="button"
        onClick={() => onCardClick(agent)}
        aria-label={`Open ${agent.name}`}
        className="absolute inset-0 z-0 rounded-xl focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-primary cursor-pointer"
      />

      <div className="relative z-10 flex items-start justify-between mb-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${agent.color || "#EC622B"}20` }}
        >
          <Bot className="w-6 h-6" style={{ color: agent.color || "#EC622B" }} />
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          <button
            type="button"
            onClick={(e) => onStartChat(agent, e)}
            className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-brand-primary"
            title="Start Chat"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          {/* Duplicate creates a new agent owned by the current user; it
              doesn't modify the source, so any viewer may duplicate. */}
          <button
            type="button"
            onClick={(e) => onDuplicate(agent, e)}
            className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          {agent.canEdit && (
            <>
              <button
                type="button"
                onClick={(e) => onEdit(agent, e)}
                className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={(e) => onDelete(agent.uuid, e)}
                disabled={deletingUuid === agent.uuid}
                className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-red-400 disabled:opacity-50"
                title="Delete"
              >
                {deletingUuid === agent.uuid ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </>
          )}
        </div>
      </div>
      <h3 className="relative z-10 pointer-events-none text-lg font-bold text-content-primary mb-2 flex items-center gap-2">
        <span className="truncate">{agent.name}</span>
        {agent.uuid === newlyCreatedUuid && (
          <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-400 font-semibold uppercase tracking-wide">
            New
          </span>
        )}
      </h3>
      <p className="relative z-10 pointer-events-none text-content-tertiary text-sm line-clamp-2">
        {agent.description || "No description"}
      </p>
      <div className="relative z-10 mt-auto pt-4 space-y-3">
        <div className="flex items-center justify-between text-xs pointer-events-none">
          <div className="flex items-center gap-3">
            <span className="text-content-quaternary">{agent.usageCount} uses</span>
          </div>
          <span className="px-2 py-1 bg-surface-secondary text-content-tertiary rounded-sm">
            {agent.model?.displayName || agent.model?.name || agent.modelId}
          </span>
        </div>
        {agent.dataCollectionIds && agent.dataCollectionIds.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-content-quaternary pointer-events-none">
            <Database className="w-3 h-3" />
            <span>{agent.dataCollectionIds.length} data sources</span>
          </div>
        )}
        <AgentProjectBadge agent={agent} className="flex-wrap" />
      </div>
    </div>
  );
}
